// ABOUTME: Hook to fetch and compose the current authenticated user (Me) with all their details.
// ABOUTME: Handles authentication errors, analytics tracking, and user property initialization.

import { useEffect, useState } from "react"
import { useQuery } from "@apollo/client/react"
import { GetMeDocument, type UserInfo } from "@/graphql/main/gql/graphql"
import authSupport from "@/model/auth/authSupport"
import { redirectToLogin } from "@/model/navigation"

import { setUserProperties } from "../ga"
import { setMixpanelUserProperties } from "../mixpanel"
import { useUser } from "./useUser"
import { Me } from "./me"
import { addLogError } from "../logger"
import { STORAGE_KEYS } from "@shared/constants/storageKeys"

interface UseMeOptions {
  /**
   * Called when the session is valid but its account is gone. Passed in, not imported: logging out
   * clears an Apollo cache, a HealthKit store and a Kratos session, all of which live above this
   * layer — the hook only knows *when* it has to happen.
   */
  onUserGone: () => void
}

export function useMe({ onUserGone }: UseMeOptions) {
  const [user, setUser] = useState<Me>()

  const userMe = useQuery(GetMeDocument, { fetchPolicy: "cache-and-network" })
  const userMeData = userMe.data?.userInfo
  const userID = userMeData?.id

  useEffect(() => {
    if (userMe.error?.message === "ErrAccessDenied") {
      if (authSupport.isSetSupportToken()) {
        addLogError("Auth support token not correct, cleaning up")
        authSupport.clean()
      }
      const path = window.location.pathname
      if (path.includes("acceptcoachrequest")) {
        localStorage.setItem(STORAGE_KEYS.BIND_TRAINER_LINK, JSON.stringify(path))
      }

      // Flag OFF → external auth redirect (unchanged); flag ON → SPA-navigate to /auth/login.
      redirectToLogin()

      return
    }

    // User still has a valid auth session but their record is gone (e.g. just
    // after accountDelete). The normal failure screen traps them in a retry
    // loop — force a full logout so Kratos clears the session too.
    if (userMe.error?.message === "ErrUserNotFound") {
      onUserGone()
    }
  }, [userMe.error, onUserGone])

  const userInfo = useUser({
    userID,
  })
  const userInfoData = userInfo.user

  // Compose me in the render that sees a new pair of answers. Until the next pair
  // arrives the last me stays: a refetch that briefly has no data keeps the app up.
  const [composedFrom, setComposedFrom] = useState<{ me: typeof userMeData; info: typeof userInfoData }>()
  if (userMeData && userInfoData && (composedFrom?.me !== userMeData || composedFrom.info !== userInfoData)) {
    setComposedFrom({ me: userMeData, info: userInfoData })
    setUser(new Me(userMeData as UserInfo, userInfoData))
  }

  useEffect(() => {
    if (!user) {
      return
    }
    setUserProperties(user.id, user.isPro, user.isCoach)
    void setMixpanelUserProperties(user.id, user.isPro, user.isCoach)
  }, [user])

  return {
    me: user,
    loading: userMe.loading || userInfo.loading,
    error: userMe.error || userInfo.error,
  }
}
